import { useEffect, useState } from 'react';
import { QueryModel, SearchModel, SortModel } from '../DataTableQuery.types';
import { QueryVariablePreprocessor } from './initialQueryVariableProcessors.types';

// "complete-in-order" does not pass the result to the next processor until the calculations have been performed by the previous processor. Ends when all processors have been processed.
// "complete-without-order" the result is passed to the next processor without waiting for the previous processor to finish. Ends when all processors have been processed.
type Mode = 'complete-in-order' | 'complete-without-order';

interface ProcessorExecutorProps {
  processors: QueryVariablePreprocessor.Function[];
  onChange: (processedValue: SearchModel<QueryModel, SortModel> | null) => void;
  options?: {
    mode?: Mode;
  };
}

export const useProcessorExecutor = ({ processors, onChange, options }: ProcessorExecutorProps) => {
  const [memoizedOptions] = useState<ProcessorExecutorProps['options']>(options || { mode: 'complete-in-order' });
  const [executionCompleted, setExecutionCompleted] = useState(false);
  const [processResults, setProcessResults] = useState<QueryVariablePreprocessor.Result[]>(
    processors.map(() => ({ status: QueryVariablePreprocessor.Status.NOT_STARTED, result: null }))
  );
  const [memoizedProcessors, setMemoizedProcessors] = useState<QueryVariablePreprocessor.Function[]>(processors);
  useEffect(() => {
    setMemoizedProcessors(processors);
    // biome-ignore lint/correctness/useExhaustiveDependencies: exception
  }, processors);

  useEffect(() => {
    setProcessResults((prevProcessResults) =>
      calculateNextState(memoizedProcessors, prevProcessResults, memoizedOptions?.mode)
    );
  }, [memoizedOptions, memoizedProcessors]);

  useEffect(() => {
    if (!executionCompleted && isAllCompleted(processResults)) {
      // The last result is the result of the last processor.
      const nextValue = processResults[processResults.length - 1];
      // There is no result. Ignore it.
      if (!nextValue) {
        return;
      }
      onChange(nextValue.result);
      setExecutionCompleted(true);
    }
  }, [processResults, executionCompleted, onChange]);
};

function calculateNextState(
  processors: QueryVariablePreprocessor.Function[],
  prevProcessResults: QueryVariablePreprocessor.Result[],
  mode: Mode = 'complete-without-order'
): QueryVariablePreprocessor.Result[] {
  const result = [...prevProcessResults];

  // biome-ignore lint/suspicious/useIterableCallbackReturn: exception
  processors.forEach((processor, index) => {
    const currentResult = result[index];

    if (!currentResult) {
      throw new Error('prevProcessResults is empty');
    }
    if (mode === 'complete-in-order') {
      if (index === 0) {
        if (isCompleted(currentResult)) {
          return;
        }
        return (result[index] = processor(null));
      }

      if (!isCompleted(result[index - 1])) {
        return;
      }
      if (!isCompleted(currentResult)) {
        return (result[index] = processor(result[index - 1] ?? null));
      }
      return;
    }
    if (isCompleted(currentResult)) {
      return;
    }
    return (result[index] = processor(result[index - 1] ?? null));
  });
  return result;
}

function isAllCompleted(processResults: QueryVariablePreprocessor.Result[]) {
  return processResults.every((result) => result.status === QueryVariablePreprocessor.Status.COMPLETED);
}

function isCompleted(processResult: QueryVariablePreprocessor.Result | undefined) {
  return processResult?.status === QueryVariablePreprocessor.Status.COMPLETED;
}
